import { showToast } from '@lib/utils/toastmaster';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { useModalRegistration } from '@stores/ModalStore';
import { useMomentMergeStore } from '@stores/MomentMerge/MomentMergeStore';
import { trackingStore } from '@stores/Tracking';
import { videoPlayerStore } from '@stores/VideoPlayer';
import { observer } from 'mobx-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { BaseMomentModal, BaseMomentModalRef } from '../BaseMomentModal';
import css from './MomentTrimModal.module.scss';

export type MomentTrimModalRef = {
  open: () => void;
  close: () => void;
};

type MomentTrimModalProps = {
  momentId: string;
  onBackToMerging: () => void;
};

export const MomentTrimModal = observer(
  forwardRef<MomentTrimModalRef, MomentTrimModalProps>(({ momentId, onBackToMerging }, ref) => {
    const libraryManager = useLibraryManager();
    const momentMergeStore = useMomentMergeStore();
    const baseModalRef = useRef<BaseMomentModalRef>(null);
    // BaseMomentModal owns its open state behind the ref, so mirror it locally — the same shape
    // AddMediaModal uses.
    const [open, setOpen] = useState(false);

    // Get the current moment data
    const currentMoment = momentMergeStore.clips.find((clip) => clip.id === momentId);

    // Get the media data for this specific moment
    const media = currentMoment
      ? libraryManager.multiLibSearch.results?.medias[currentMoment.mediaId]
      : null;

    // `open` alone is NOT "visible": the `return null` below leaves this component mounted (the
    // parent keeps it alive for as long as `trimmingMomentId` is set, and nothing resets that on
    // this path), so `open` would stay true with nothing on screen. Both inputs can vanish
    // underneath us — the clip can leave `momentMergeStore.clips`, and `multiLibSearch.results`
    // goes null on any failed refetch, since DataFrame doesn't keep data on fetch error. Either
    // one used to strand 'moment-edit' on the stack, which (it isn't drag-drop allow-listed)
    // silently killed drag-drop upload and the ⌘F shortcut until the merge flow unmounted.
    useModalRegistration('moment-edit', open && !!currentMoment && !!media);

    const handleConfirmTrimmedMoment = async () => {
      if (!currentMoment) {
        showToast('moment-not-found');
        return;
      }

      try {
        // Get the current moment time range from the video player store
        const currentMomentRange = videoPlayerStore.moment;
        if (!currentMomentRange) {
          showToast('moment-range-not-selected');
          return;
        }

        // Update the clip in the store
        momentMergeStore.updateClipTimestamps(
          momentId,
          currentMomentRange.start,
          currentMomentRange.end,
        );
        trackingStore.momentTrimConfirmed({ momentId });

        baseModalRef.current?.close();
        onBackToMerging();
      } catch (error) {
        showToast('moment-update-failed', { error });
      }
    };

    const handleBackToMerging = () => {
      trackingStore.momentTrimCancelled({ momentId });
      baseModalRef.current?.close();
      onBackToMerging();
    };

    const footerContent = (
      <>
        <button className={css.backButton} onClick={handleBackToMerging}>
          Back to merging
        </button>
        <button
          className={css.confirmButton}
          onClick={handleConfirmTrimmedMoment}
          disabled={libraryManager.renderer.isRendering}
        >
          Confirm trimmed moment
        </button>
      </>
    );

    useImperativeHandle(ref, () => ({
      open: () => baseModalRef.current?.open(),
      close: () => baseModalRef.current?.close(),
    }));

    // Don't render if we don't have the required data
    if (!currentMoment || !media) {
      return null;
    }

    return (
      <BaseMomentModal
        ref={baseModalRef}
        media={media}
        startTime={currentMoment.startTime}
        endTime={currentMoment.endTime}
        onOpen={() => {
          setOpen(true);
          trackingStore.momentTrimOpened({ momentId });
        }}
        onClose={() => setOpen(false)}
        footerContent={footerContent}
        className={css.momentTrimModal}
        isTranscriptOnly={libraryManager.isMediaTranscriptOnly(media)}
      />
    );
  }),
);
